// Human Capital Management (HCM) canonical types
// These interfaces are used by importers, services and APIs to represent
// normalized HCM data independent of source systems.

import { z } from 'zod';

import type { CreateVendorRequest } from './ap.js';
import type { OrderedQuery, PaginatedResponse, PaginationQuery, SortDirection } from './common.js';
import type {
  AccountType,
  HcmCollaboratorRegime,
  HcmEmploymentStatus,
  HcmPayrollRunStatus,
  HcmPayrollRunType,
  ResourceAccessMode,
  RowStatus,
  TaxIdType,
} from './generated/enums.js';
import { computeNextChildAccountCode, isDirectNumericChildAccountCode } from './gl/account-code.js';
import type { PayrollDeductionDestinationType } from './tenant/tenant-settings.js';

export type PayrollAccessMode = ResourceAccessMode;

export interface PayrollAccessMember {
  organizationMembershipId: string;
  userId: string;
  name: string;
  email: string;
  roleSlugs: string[];
  hasAccess: boolean;
  hasDirectAccess: boolean;
  groupNames: string[];
}

export interface PayrollAccessGroup {
  groupId: string;
  name: string;
  description: string | null;
  hasAccess: boolean;
}

/** Public API contract for the tenant-wide payroll confidentiality policy. */
export interface PayrollAccessResponse {
  accessMode: PayrollAccessMode;
  members: PayrollAccessMember[];
  groups: PayrollAccessGroup[];
}

/**
 * The caller's own payroll visibility, as the API's payroll-protected routes will judge it.
 * A screen reads this to withhold a write (an employee bank account, a payroll row) it
 * knows the API would refuse with `PAYROLL_ACCESS_REQUIRED`.
 */
export interface PayrollScopeResponse {
  payrollScope: 'unrestricted' | 'restricted';
}

export type HcmEmploymentStatusCanonical = 'ACTIVE' | 'INACTIVE' | 'TERMINATED' | 'SUSPENDED';

export type HcmEmployeeListOrderBy =
  | 'employee'
  | 'type'
  | 'taxId'
  | 'position'
  | 'department'
  | 'costCenter'
  | 'payrollRunCount'
  | 'lastPaymentDate'
  | 'grossSalary'
  | 'status';

export interface HcmEmployeeListRequest
  extends PaginationQuery, OrderedQuery<HcmEmployeeListOrderBy> {
  statuses?: HcmEmploymentStatus[];
  status?: HcmEmploymentStatus;
  search?: string;
  entityId?: string;
  department?: string;
  position?: string;
}

export interface HcmEmployeePayrollRecurringBill {
  payrollRecurringBillId: string;
  payrollRecurringBillName: string;
  departmentId: string | null;
  departmentName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  payrollRunCount: number;
  lastPaymentDate: string | null;
  grossSalary: number | null;
  scheduledTerminationDate: string | null;
}

export interface HcmEmployeeListItemResponse {
  employeeId: string;
  vendorId: string | null;
  employeeNumber: string | null;
  name: string;
  entityName: string | null;
  employeeStatus: HcmEmploymentStatus;
  contractStatus: HcmEmploymentStatus | null;
  jobTitle: string | null;
  orgUnitName: string | null;
  departmentId: string | null;
  departmentName: string | null;
  costCenterId: string | null;
  costCenterName: string | null;
  taxId: string | null;
  email: string | null;
  payrollMetricsVisible: boolean;
  payrollRunCount: number;
  lastPaymentDate: string | null;
  grossSalary: number | null;
  scheduledTerminationDate: string | null;
  /** Active payroll recurring bills represented as subrows below this PJ collaborator. */
  payrollRecurringBills?: HcmEmployeePayrollRecurringBill[];
}

export interface HcmEmployeeListResponse extends PaginatedResponse<HcmEmployeeListItemResponse> {
  payrollMetricsVisible: boolean;
}

/** The Colaboradores listing's employment types: CLT, or PJ (a collaborator vendor). */
export const HCM_EMPLOYEE_LISTING_TYPES = ['CLT', 'PJ'] as const;
export type HcmEmployeeListingType = (typeof HCM_EMPLOYEE_LISTING_TYPES)[number];

/**
 * `GET /hcm/employees/listing` and `/hcm/employees/status-counts` query: the shared listing params
 * (`filtersToQueryParams`), one per column under the column's id, lists comma-separated.
 */
export interface HcmEmployeeListingQuery {
  page?: number;
  limit?: number;
  sortBy?: HcmEmployeeListOrderBy;
  sortDirection?: SortDirection;
  /** The status shown in the Situação column (contract status first). */
  status?: HcmEmploymentStatus[];
  type?: HcmEmployeeListingType[];
  /** Restrict to these employees (the Colaborador column's picks); still tenant-scoped. */
  employee?: string[];
  /** Restrict to these legal entities (the "Entidade" filter). */
  entity?: string[];
  /** Restrict to these cost centers (an employee's own, or one of its PJ recurring bills'). */
  costCenter?: string[];
  /** CPF/CNPJ contains this, punctuation ignored. */
  taxId?: string;
  /** Job title contains this, case-insensitive. */
  position?: string;
  /** Department or org unit name contains this, case-insensitive. */
  department?: string;
  /** Name, number, e-mail, tax id or entity contains this (the Colaborador filter's search). */
  search?: string;
  /** Last payment date range (yyyy-MM-dd, inclusive). */
  lastPaymentDateFrom?: string;
  lastPaymentDateTo?: string;
  /** Payroll run count: equal to, greater than, at least, less than, at most. */
  payrollRunCount?: number;
  payrollRunCountGt?: number;
  payrollRunCountGte?: number;
  payrollRunCountLt?: number;
  payrollRunCountLte?: number;
  /** Gross salary: equal to, greater than, at least, less than, at most. */
  grossSalary?: number;
  grossSalaryGt?: number;
  grossSalaryGte?: number;
  grossSalaryLt?: number;
  grossSalaryLte?: number;
}

export interface HcmEmployeeListingResponse {
  items: HcmEmployeeListItemResponse[];
  total: number;
  page: number;
  limit: number;
  payrollMetricsVisible: boolean;
}

/** Employees per status over every filter but the status, plus `all`. */
export type HcmEmployeeStatusCounts = Record<HcmEmploymentStatus | 'all', number>;

/**
 * The Folha de pagamento source filter's families, as the web's `getPayrollSourceLabelKey` reads
 * an import source: `unknown` is any other import source, `manual` a run with no import source.
 */
export const HCM_PAYROLL_RUN_LISTING_SOURCES = [
  'protheus',
  'tako',
  'dominio',
  'metadados',
  'unknown',
  'manual',
] as const;
export type HcmPayrollRunListingSource = (typeof HCM_PAYROLL_RUN_LISTING_SOURCES)[number];

/** Columns the Folha de pagamento listing sorts by, inside each competência. */
export const HCM_PAYROLL_RUN_LISTING_SORT_FIELDS = ['gross', 'net', 'employees'] as const;
export type HcmPayrollRunListingSortField = (typeof HCM_PAYROLL_RUN_LISTING_SORT_FIELDS)[number];

/**
 * `GET /hcm/payroll-runs/listing` and `/hcm/payroll-runs/status-counts` query: the shared listing
 * params (`filtersToQueryParams`), one per column under the column's id, lists comma-separated.
 */
export interface HcmPayrollRunListingQuery {
  page?: number;
  limit?: number;
  sortBy?: HcmPayrollRunListingSortField;
  sortDirection?: SortDirection;
  status?: HcmPayrollRunStatus[];
  type?: HcmPayrollRunType[];
  /** Restrict to these legal entities. */
  entity?: string[];
  source?: HcmPayrollRunListingSource[];
  /** Competência range: the month of each date (yyyy-MM-dd), both ends included. */
  periodFrom?: string;
  periodTo?: string;
  /** Gross total: equal to, greater than, at least, less than, at most. */
  gross?: number;
  grossGt?: number;
  grossGte?: number;
  grossLt?: number;
  grossLte?: number;
  /** Net pay total: equal to, greater than, at least, less than, at most. */
  net?: number;
  netGt?: number;
  netGte?: number;
  netLt?: number;
  netLte?: number;
  /** Employees in the run: equal to, greater than, at least, less than, at most. */
  employees?: number;
  employeesGt?: number;
  employeesGte?: number;
  employeesLt?: number;
  employeesLte?: number;
}

/** One payroll run as the Folha de pagamento listing shows it (no employee results). */
export interface HcmPayrollRunListingItem {
  payrollRunId: string;
  entityId: string;
  entityName: string | null;
  /** Competência, `YYYY-MM`. */
  periodId: string;
  /** yyyy-MM-dd. */
  runDate: string;
  status: HcmPayrollRunStatus;
  runType: HcmPayrollRunType;
  description: string | null;
  externalSource: string | null;
  totalGrossAmount: number;
  totalNetPayAmount: number;
  totalEmployerChargesAmount: number;
  /** ISO 4217 currency of the run's amounts. */
  currencyCode: string;
  employeeCount: number;
  postingBlockedReason: string | null;
  postingBlockedReasonCode: string | null;
  canReprocess: boolean;
}

/**
 * One competência over the whole filtered result, not only the page: its run count, gross and net
 * (voided runs left out) and how many runs still wait on someone (draft, processing, processed).
 */
export interface HcmPayrollRunCompetenciaTotals {
  count: number;
  grossAmount: number;
  netPayAmount: number;
  pendingCount: number;
}

export interface HcmPayrollRunListingResponse {
  items: HcmPayrollRunListingItem[];
  total: number;
  page: number;
  limit: number;
  /** Keyed by competência (`YYYY-MM`, '' for a run without one), for the competências on the page. */
  competencias: Record<string, HcmPayrollRunCompetenciaTotals>;
}

/** Payroll runs per status over every filter but the status, plus `all`. */
export type HcmPayrollRunStatusCounts = Record<HcmPayrollRunStatus | 'all', number>;

export interface HcmEmployeeCanonical {
  /** External employee identifier in the source system */
  externalEmployeeId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;
  /** Optional external person/party identifier in the source system */
  personExternalId?: string;

  name: string;
  employeeNumber?: string;
  taxId?: string;
  birthDate?: string;
  email?: string;
  phoneNumber?: string;

  employmentStatus?: HcmEmploymentStatusCanonical;
  jobTitle?: string;

  orgUnitCode?: string;
  orgUnitName?: string;
  orgUnitEffectiveFrom?: string;

  dependentName?: string;
  dependentTaxId?: string;
  dependentBirthDate?: string;
  dependentRelationship?: string;
}

export interface HcmEmployeeDependentResponse {
  dependentId: string;
  relationship: string | null;
  party: {
    partyId: string;
    name: string;
    legalName: string;
    birthDate: string | null;
    taxIdentifiers: {
      type: TaxIdType;
      value: string;
      isPrimary: boolean;
    }[];
  };
}

export interface HcmEmployeeDetailResponse {
  employeeId: string;
  employeeNumber: string | null;
  status: string;
  entity?: {
    entityId: string;
    name: string;
  } | null;
  party: {
    partyId: string;
    name: string;
    legalName: string;
    birthDate: string | null;
    email: string | null;
    phoneNumber: string | null;
    taxIdentifiers?: {
      type: TaxIdType;
      value: string;
      isPrimary: boolean;
    }[];
    bankAccounts?: {
      bankAccountId: string;
      paymentMethod: string | null;
      bankCode: string | null;
      bankName: string | null;
      agency: string | null;
      accountNumber: string | null;
      accountType: string | null;
      pixEnabled: boolean;
      pixKey: string | null;
      isDefault: boolean;
      isPrimary: boolean;
    }[];
  };
  dependents: HcmEmployeeDependentResponse[];
  lastKnownPositionFromPayroll?: string | null;
  lastKnownEmploymentStatusFromPayroll?: string | null;
}

export interface HcmEmploymentContractCanonical {
  /** External contract identifier in the source system */
  externalContractId: string;
  /** External employee identifier, must match HcmEmployeeCanonical.externalEmployeeId */
  employeeExternalId: string;

  /** Start date in ISO YYYY-MM-DD */
  startDate: string;
  /** Optional end date in ISO YYYY-MM-DD */
  endDate?: string;

  employmentStatus?: HcmEmploymentStatusCanonical;
  jobTitle?: string;
  contractType?: string;
}

export interface HcmOrgUnitCanonical {
  /** External org unit identifier in the source system */
  externalOrgUnitId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;

  code: string;
  name: string;
  description?: string;

  parentExternalOrgUnitId?: string;
}

export interface HcmPositionCanonical {
  /** External position identifier in the source system */
  externalPositionId: string;
  /** External identifier or code for the legal entity */
  entityExternalId: string;

  code: string;
  name: string;
  description?: string;
}

export interface HcmOrgUnitAssignmentCanonical {
  /** External assignment identifier in the source system */
  externalAssignmentId: string;
  /** External employee identifier */
  employeeExternalId: string;
  /** External org unit identifier */
  orgUnitExternalId: string;
  /** External position identifier, if available */
  positionExternalId?: string;

  /** Effective from date in ISO YYYY-MM-DD */
  effectiveFrom: string;
  /** Effective to date in ISO YYYY-MM-DD */
  effectiveTo?: string;
}

/** Structured issue in a payroll file validation report. */
export interface PayrollFileValidationIssue {
  code: string;
  message: string;
  /** Structured facts behind the issue (CNPJ_MISMATCH: `fileCnpj`, `entityCnpj`), for the UI copy. */
  context?: Record<string, string | number>;
}

/** Where an employee of a validated file will be paid from. */
export interface PayrollFileValidationPayment {
  /** FILE: the file carries the PIX key or bank account; CADASTRO: the employee record holds one. */
  source: 'FILE' | 'CADASTRO';
  method: 'PIX' | 'BANK' | null;
  /** Masked PIX key, or "bank branch · account" — null for CADASTRO. */
  detail: string | null;
}

/** One employee row of a validated file, as it will be imported. */
export interface PayrollFileValidationEmployee {
  name: string;
  /** CPF masked to its middle digits (`***.482.117-**`); null when the file carries none. */
  cpfMasked: string | null;
  employeeNumber: string | null;
  role: string | null;
  gross: number;
  deductions: number;
  net: number;
  /** Null: no payment instrument in the file nor in the cadastro (the run imports; payment waits). */
  payment: PayrollFileValidationPayment | null;
}

/**
 * Where a rubric of a validated file posts. Only deductions resolve here: earnings and employer
 * charges follow the payroll account mapping (per cost center and CoA version) at approve.
 */
export type PayrollFileValidationRubricDestination =
  | { type: 'TAX' }
  | { type: 'CLASS'; deductionClass: string }
  | {
      type: 'MAPPING';
      destinationType: string;
      account: { code: string; name: string } | null;
    }
  | { type: 'PAYROLL_SETTINGS' }
  /** RUBRIC_UNMAPPED: the run imports; approve waits for a mapping. */
  | { type: 'UNMAPPED' };

/** One rubric of a validated file, summed over its employees. */
export interface PayrollFileValidationRubric {
  code: string | null;
  name: string;
  kind: 'EARNING' | 'DEDUCTION' | 'EMPLOYER_CHARGE' | 'PROVISION_MOVEMENT';
  total: number;
  destination: PayrollFileValidationRubricDestination;
}

/**
 * Dry-run validation report for an uploaded payroll file.
 * Produced by `POST /hcm/payroll-runs/validate` (API: `CanonicalPayrollImportService.validateFile`);
 * consumed by the web import dialog preflight.
 */
export interface PayrollFileValidationReport {
  /** True when the file would import cleanly AND has no approve blockers. */
  ok: boolean;
  /** True when the file parses and would import (warnings allowed). */
  importable: boolean;
  source: string | null;
  period: string | null;
  runType: string | null;
  /** False → import lands but approve blocks with RUN_TYPE_UNSUPPORTED. */
  runTypePostable: boolean | null;
  fileCnpj: string | null;
  cnpjMatchesEntity: boolean | null;
  employeeCount: number;
  employeesWithCostCenter: number;
  lineItemCount: number;
  totals: { gross: number; deductions: number; net: number } | null;
  /** Same hash already uploaded — import would dedupe to the existing file. */
  isDuplicateFile: boolean;
  /** Existing run for entity+period+source+runType (re-import replaces it; POSTED blocks). */
  existingRun: { payrollRunId: string; status: string } | null;
  /** OPEN period covers the competência end date (else approve needs a later journalDate). */
  openPeriodCoversCompetencia: boolean | null;
  /** Non-tax deduction rubrics without a GL mapping (RUBRIC_UNMAPPED gate). */
  unmappedDeductionRubrics: string[];
  /** The file's employees as they will be imported (empty when the file does not parse). */
  employees: PayrollFileValidationEmployee[];
  /** The file's rubrics, unmapped deductions first (empty when the file does not parse). */
  rubrics: PayrollFileValidationRubric[];
  errors: PayrollFileValidationIssue[];
  warnings: PayrollFileValidationIssue[];
}

/** Tax totals returned with payroll-run details. */
export interface HcmPayrollTaxSummary {
  inssAmount: number;
  fgtsAmount: number;
  irrfAmount: number;
  issWithheld: number;
  totalTaxAmount: number;
}

// ---------------------------------------------------------------------------
// Payroll deduction rubric mapping — destination account families + AI suggest
// ---------------------------------------------------------------------------

/**
 * GL account-type family allowed per deduction destination.
 * TAX is intentionally absent — tax deductions use the System Account Mapping
 * slots and require no account on the deduction mapping screen.
 *
 * Shared by the web AccountSelector filtering and the API save-path validation
 * so a REPASSE rubric can never point at an expense account (and so on).
 */
export const PAYROLL_DEDUCTION_DESTINATION_ACCOUNT_TYPES = {
  REPASSE: ['LIABILITY'],
  ASSET_SETTLEMENT: ['ASSET'],
  EXPENSE_RECOVERY: ['EXPENSE', 'COGS', 'OTHER_EXPENSE'],
} as const satisfies Record<
  Exclude<PayrollDeductionDestinationType, 'TAX'>,
  readonly AccountType[]
>;

/** Allowed account types for a destination; empty for TAX (no account required). */
export function allowedAccountTypesForDeductionDestination(
  destinationType: PayrollDeductionDestinationType,
): readonly AccountType[] {
  if (destinationType === 'TAX') return [];
  return PAYROLL_DEDUCTION_DESTINATION_ACCOUNT_TYPES[destinationType];
}

/**
 * Request for `POST /hcm/payroll-runs/deduction-rubrics/suggest`.
 * The client sends the CURRENT (possibly unsaved) destination per unmapped
 * rubric so suggestions match what the user sees on screen. Rubrics omitted
 * here default to REPASSE. Already-mapped rubrics are never suggested.
 */
export interface SuggestDeductionRubricMappingsRequest {
  versionId?: string | null;
  entityId?: string | null;
  bookId?: string | null;
  rubrics?: {
    providerCode: string;
    destinationType: PayrollDeductionDestinationType;
  }[];
}

/** Structured-output schema returned by the deduction mapping LLM agent. */
export const DeductionRubricMappingSuggestionSchema = z
  .object({
    providerCode: z.string(),
    /** glAccountId of the chosen candidate, or null when no candidate fits. */
    glAccountId: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    reason: z.string(),
  })
  .strict();

export const DeductionRubricMappingSuggestionResultSchema = z
  .object({
    suggestions: z.array(DeductionRubricMappingSuggestionSchema),
  })
  .strict();

export type DeductionRubricMappingSuggestion = z.infer<
  typeof DeductionRubricMappingSuggestionSchema
>;
export type DeductionRubricMappingSuggestionResult = z.infer<
  typeof DeductionRubricMappingSuggestionResultSchema
>;

/** Machine-readable reason a rubric received no account suggestion. */
export const DeductionRubricNoSuggestionCode = {
  /** Trigram pre-filter found no account of the allowed family in the CoA. */
  NO_CANDIDATES: 'NO_CANDIDATES',
  /** The LLM judged no candidate account a suitable match. */
  LLM_NO_MATCH: 'LLM_NO_MATCH',
  /** The LLM response did not include this rubric at all. */
  LLM_MISSING: 'LLM_MISSING',
  /** The LLM picked an account outside the candidate list (rejected server-side). */
  LLM_INVALID_ACCOUNT: 'LLM_INVALID_ACCOUNT',
} as const;
export type DeductionRubricNoSuggestionCode =
  (typeof DeductionRubricNoSuggestionCode)[keyof typeof DeductionRubricNoSuggestionCode];

/**
 * One suggestion for an unmapped rubric, enriched for client prefill.
 * Every requested rubric is echoed back: rubrics the pipeline could not place
 * carry `glAccountId: null` plus `noSuggestionCode` and a human-readable
 * `reason` so the UI can explain why the row was not prefilled.
 */
export interface DeductionRubricAccountSuggestion {
  providerCode: string;
  /** Destination the suggestion was computed for (echo of the request/default). */
  destinationType: PayrollDeductionDestinationType;
  /** null when no account could be suggested — see noSuggestionCode/reason. */
  glAccountId: string | null;
  accountCode: string | null;
  accountName: string | null;
  confidence: number;
  /** Human-readable explanation (pt-BR) — from the LLM or a server fallback. */
  reason: string;
  /** Present only when glAccountId is null. */
  noSuggestionCode?: DeductionRubricNoSuggestionCode;
}

/** Response of `POST /hcm/payroll-runs/deduction-rubrics/suggest`. */
export interface SuggestDeductionRubricMappingsResponse {
  suggestions: DeductionRubricAccountSuggestion[];
}

// ---------------------------------------------------------------------------
// Payroll GL root resolution — rubric type → existing postable descendant
// ---------------------------------------------------------------------------

/**
 * Generic payroll rubric types debited under a payroll result root account.
 * Mirrors `AccountService.PAYROLL_GENERIC_CHILD_TYPES` (apps/api) and the
 * `.01`–`.21` auto-created generic child suffixes (`.10` and `.11` retired, never reused).
 */
export const HcmPayrollRubricType = {
  SALARY: 'SALARY',
  VACATION: 'VACATION',
  FGTS: 'FGTS',
  THIRTEENTH: 'THIRTEENTH',
  INSS_EMPLOYER: 'INSS_EMPLOYER',
  BENEFIT_VR: 'BENEFIT_VR',
  BENEFIT_VT: 'BENEFIT_VT',
  VACATION_PROVISION: 'VACATION_PROVISION',
  THIRTEENTH_PROVISION: 'THIRTEENTH_PROVISION',
  /**
   * Charges on the provisions, one nature per component (ADR 0025 decision 12), mirroring the
   * liability side (`HCM_PAYROLL_*_CHARGES_INSS_PAYABLE` / `…_FGTS_PAYABLE`): the employer INSS
   * (CPP + RAT × FAP + terceiros, Lei 8.212/1991 art. 22 I-II) and the FGTS (Lei 8.036/1990 art.
   * 15) accrued on each provision. The ECD/ECF plano referencial keeps the INSS and the FGTS
   * encargos apart, so the expense side does too; a grouped view is built on top of them.
   */
  VACATION_CHARGES_INSS: 'VACATION_CHARGES_INSS',
  VACATION_CHARGES_FGTS: 'VACATION_CHARGES_FGTS',
  THIRTEENTH_CHARGES_INSS: 'THIRTEENTH_CHARGES_INSS',
  THIRTEENTH_CHARGES_FGTS: 'THIRTEENTH_CHARGES_FGTS',
  PRO_LABORE: 'PRO_LABORE',
  TERMINATION: 'TERMINATION',
  AUTONOMOUS_SERVICES: 'AUTONOMOUS_SERVICES',
  OTHER_EARNINGS: 'OTHER_EARNINGS',
  BENEFIT_HEALTH: 'BENEFIT_HEALTH',
  /**
   * Bolsa-estágio (Lei 11.788/2008 arts. 3 and 12): the intern has no employment bond, so the
   * stipend is not salary and carries no INSS, FGTS or 13º; it has its own nature so it never
   * shares the salary account (ADR 0022).
   */
  INTERNSHIP_STIPEND: 'INTERNSHIP_STIPEND',
  /**
   * Salário-maternidade and salário-família: benefits of the Previdência Social that the
   * company only pays on the INSS's behalf and compensates against the contributions due
   * (Lei 8.213/1991 art. 72 §1 for the maternidade, art. 68 for the família). They are not
   * remuneration expense of the company, so this class has NO expense child under the root
   * (it is not one of the fixed natures, ADR 0022): its debit is the INSS payable.
   */
  SOCIAL_SECURITY_BENEFIT: 'SOCIAL_SECURITY_BENEFIT',
} as const;
export type HcmPayrollRubricType = (typeof HcmPayrollRubricType)[keyof typeof HcmPayrollRubricType];

/**
 * Rubric classes that are NOT expense natures under a payroll root: they never get a
 * `<root>.NN` child, a resolution row or a place in the natures list (ADR 0022).
 */
export type HcmPayrollNonNatureRubricType = typeof HcmPayrollRubricType.SOCIAL_SECURITY_BENEFIT;

/** The fixed expense natures under a payroll root (`AccountService.PAYROLL_GENERIC_CHILD_TYPES`). */
export type HcmPayrollNatureType = Exclude<HcmPayrollRubricType, HcmPayrollNonNatureRubricType>;

/** Every nature in the fixed list order; excludes the classes that are not natures. */
export const HCM_PAYROLL_NATURE_TYPES: readonly HcmPayrollNatureType[] = (
  Object.values(HcmPayrollRubricType) as HcmPayrollRubricType[]
).filter((type): type is HcmPayrollNatureType => type !== 'SOCIAL_SECURITY_BENEFIT');

/** Portuguese labels used in journal descriptions, the UI and the LLM prompt. */
export const HCM_PAYROLL_RUBRIC_TYPE_LABELS: Record<HcmPayrollRubricType, string> = {
  SALARY: 'Salários',
  VACATION: 'Férias',
  FGTS: 'FGTS',
  THIRTEENTH: '13º Salário',
  INSS_EMPLOYER: 'INSS Patronal',
  BENEFIT_VR: 'Vale Refeição',
  BENEFIT_VT: 'Vale Transporte',
  VACATION_PROVISION: 'Provisão de Férias',
  THIRTEENTH_PROVISION: 'Provisão de 13º',
  VACATION_CHARGES_INSS: 'INSS s/ Prov. Férias',
  VACATION_CHARGES_FGTS: 'FGTS s/ Prov. Férias',
  THIRTEENTH_CHARGES_INSS: 'INSS s/ Prov. 13º',
  THIRTEENTH_CHARGES_FGTS: 'FGTS s/ Prov. 13º',
  PRO_LABORE: 'Pró-labore',
  /** Indemnity verbas of a rescisão (aviso prévio indenizado, multa FGTS) — C-HCM-6. */
  TERMINATION: 'Indenizações Trabalhistas',
  AUTONOMOUS_SERVICES: 'Serviços de autônomos PF',
  OTHER_EARNINGS: 'Outros proventos',
  BENEFIT_HEALTH: 'Plano de Saúde',
  INTERNSHIP_STIPEND: 'Bolsa de estágio',
  SOCIAL_SECURITY_BENEFIT: 'Benefício previdenciário (a compensar)',
};

/**
 * `externalId` prefix of the generic nature children Alchemy ERP creates under payroll roots
 * (`hcm-payroll-generic-<HcmPayrollRubricType>`). Lets the adoption rule tell its own
 * children from the tenant's accounts.
 */
export const HCM_PAYROLL_GENERIC_CHILD_EXTERNAL_ID_PREFIX = 'hcm-payroll-generic-';

/**
 * Fixed code suffix and default name of the generic child Alchemy ERP creates for each nature
 * directly under a payroll root (`<root>.NN`, ADR 0022 §1). Shared by the GL account service
 * (creation) and the HCM adoption rule (recognition).
 */
export const HCM_PAYROLL_RUBRIC_TYPE_CHILD_SPEC: Record<
  HcmPayrollNatureType,
  { suffix: string; name: string }
> = {
  SALARY: { suffix: '.01', name: 'Salários' },
  VACATION: { suffix: '.02', name: 'Férias' },
  FGTS: { suffix: '.03', name: 'FGTS' },
  THIRTEENTH: { suffix: '.04', name: '13º Salário' },
  INSS_EMPLOYER: { suffix: '.05', name: 'INSS Patronal' },
  BENEFIT_VR: { suffix: '.06', name: 'Vale Refeição' },
  BENEFIT_VT: { suffix: '.07', name: 'Vale Transporte' },
  VACATION_PROVISION: { suffix: '.08', name: 'Provisão de Férias' },
  THIRTEENTH_PROVISION: { suffix: '.09', name: 'Provisão de 13º Salário' },
  // `.10` and `.11` held the combined charges natures retired by ADR 0025 decision 12; they are
  // never reused, so a suffix always names the same nature under every root.
  VACATION_CHARGES_INSS: { suffix: '.18', name: 'INSS s/ Prov. Férias' },
  VACATION_CHARGES_FGTS: { suffix: '.19', name: 'FGTS s/ Prov. Férias' },
  THIRTEENTH_CHARGES_INSS: { suffix: '.20', name: 'INSS s/ Prov. 13º' },
  THIRTEENTH_CHARGES_FGTS: { suffix: '.21', name: 'FGTS s/ Prov. 13º' },
  PRO_LABORE: { suffix: '.12', name: 'Pró-labore' },
  TERMINATION: { suffix: '.13', name: 'Indenizações Trabalhistas' },
  AUTONOMOUS_SERVICES: { suffix: '.14', name: 'Serviços de autônomos PF' },
  OTHER_EARNINGS: { suffix: '.15', name: 'Outros proventos' },
  BENEFIT_HEALTH: { suffix: '.16', name: 'Plano de Saúde' },
  INTERNSHIP_STIPEND: { suffix: '.17', name: 'Bolsa de Estágio' },
};

/**
 * Code of the generic nature child Alchemy ERP creates under a payroll root.
 *
 * The suffix follows the tenant's own convention: when the root already has children whose code
 * is `<root><separator><digits>` (e.g. `4.2.2.01.000001`), the new code uses the same separator
 * and zero-padding width and the next free number (`4.2.2.01.000026`), instead of the spec
 * suffix (`4.2.2.01.08`) that would sit next to them as an odd one out. The spec suffix is kept
 * only when the root has no such children (ADR 0022 §1: a root Alchemy ERP fills itself gets
 * the same fixed suffixes for every tenant).
 *
 * Pure: shared by the GL account service (creation) and the natures drawer (preview, through
 * `plannedCode` of the root-resolution API), so both always agree.
 *
 * @param rootCode    code of the payroll root
 * @param specSuffix  `HCM_PAYROLL_RUBRIC_TYPE_CHILD_SPEC[type].suffix`
 * @param siblingCodes codes of the root's direct children that set the convention (the tenant's
 *                    own accounts; pass Alchemy-created generic children too and they just
 *                    count as siblings)
 * @param takenCodes  every code already used under the root (defaults to `siblingCodes`); the
 *                    result never collides with one
 */
export function deriveGenericChildCode(params: {
  rootCode: string;
  specSuffix: string;
  siblingCodes: readonly string[];
  takenCodes?: readonly string[];
}): string {
  const { rootCode, specSuffix } = params;
  const numericSiblings = params.siblingCodes.filter((code) =>
    isDirectNumericChildAccountCode(code, rootCode),
  );
  const taken = new Set(params.takenCodes ?? params.siblingCodes);
  if (numericSiblings.length === 0) {
    const specCode = `${rootCode}${specSuffix}`;
    if (!taken.has(specCode)) return specCode;
    // The fixed code is held by another account: continue after what is there.
    numericSiblings.push(...[...taken].filter((c) => isDirectNumericChildAccountCode(c, rootCode)));
  }
  const known = [...numericSiblings];
  let code = computeNextChildAccountCode(rootCode, known);
  while (taken.has(code)) {
    known.push(code);
    code = computeNextChildAccountCode(rootCode, known);
  }
  return code;
}

const HCM_PAYROLL_RUBRIC_TYPE_TUPLE = [
  'SALARY',
  'VACATION',
  'FGTS',
  'THIRTEENTH',
  'INSS_EMPLOYER',
  'BENEFIT_VR',
  'BENEFIT_VT',
  'VACATION_PROVISION',
  'THIRTEENTH_PROVISION',
  'VACATION_CHARGES_INSS',
  'VACATION_CHARGES_FGTS',
  'THIRTEENTH_CHARGES_INSS',
  'THIRTEENTH_CHARGES_FGTS',
  'PRO_LABORE',
  'TERMINATION',
  'AUTONOMOUS_SERVICES',
  'OTHER_EARNINGS',
  'BENEFIT_HEALTH',
  'INTERNSHIP_STIPEND',
] as const satisfies readonly HcmPayrollRubricType[];

/** Structured-output schema returned by the payroll root resolution LLM agent. */
export const PayrollRootResolutionSuggestionSchema = z
  .object({
    rubricType: z.enum(HCM_PAYROLL_RUBRIC_TYPE_TUPLE),
    /** MAP = use an existing candidate; CREATE = no suitable existing account. */
    action: z.enum(['MAP', 'CREATE']),
    /** glAccountId of the chosen candidate (MAP only, null for CREATE). */
    glAccountId: z.string().nullable(),
    confidence: z.number().min(0).max(1),
    reason: z.string(),
  })
  .strict();

export const PayrollRootResolutionResultSchema = z
  .object({
    resolutions: z.array(PayrollRootResolutionSuggestionSchema),
  })
  .strict();

export type PayrollRootResolutionSuggestion = z.infer<typeof PayrollRootResolutionSuggestionSchema>;
export type PayrollRootResolutionResult = z.infer<typeof PayrollRootResolutionResultSchema>;

/**
 * How one nature under a payroll root is resolved (ADR 0022).
 *
 * - `ADOPTED`        an existing postable descendant of the root holds the nature (AUTO/MAP).
 * - `CREATED`        Alchemy ERP created `<root>.NN` for it (AUTO/CREATE with an account).
 * - `PENDING_CREATE` no existing account reads as the nature; `<root>.NN` is created at the
 *                    first posting (AUTO/CREATE without an account yet).
 * - `AMBIGUOUS`      two or more descendants read as the nature; a person picks one
 *                    (`PUT gl-root-resolution/:root`). No row is persisted until then; posting
 *                    uses `<root>.NN` meanwhile.
 * - `MANUAL`         a person pinned the account (MANUAL/MAP). Never overwritten by the rule.
 */
export type HcmPayrollRootNatureStatus =
  'ADOPTED' | 'CREATED' | 'PENDING_CREATE' | 'AMBIGUOUS' | 'MANUAL';

/** A postable descendant of the root offered for an AMBIGUOUS tie-break. */
export interface HcmPayrollRootNatureCandidate {
  glAccountId: string;
  code: string;
  name: string;
}

/** One of the 19 natures under one payroll root, with the account it resolves to. */
export interface HcmPayrollRootNatureItem {
  /** Always a nature (`HcmPayrollNatureType`); `SOCIAL_SECURITY_BENEFIT` is never listed. */
  rubricType: HcmPayrollRubricType;
  /** Portuguese label (`HCM_PAYROLL_RUBRIC_TYPE_LABELS`). */
  label: string;
  status: HcmPayrollRootNatureStatus;
  /** Account in use: adopted, created or pinned. Null for PENDING_CREATE and for AMBIGUOUS
   * while no generic child exists yet. */
  glAccountId: string | null;
  code: string | null;
  name: string | null;
  /** Only for `AMBIGUOUS`: the descendants that read as this nature, sorted by code. */
  candidates?: HcmPayrollRootNatureCandidate[];
  /**
   * Only for `PENDING_CREATE`/`AMBIGUOUS` without an account yet: the code the account will get
   * when created, derived from the root's existing children (`deriveGenericChildCode`). A
   * forecast: posting takes the next free number in creation order.
   */
  plannedCode?: string | null;
}

/** A postable account under the root that no nature uses (the payroll never posts to it). */
export interface HcmPayrollRootOrphanAccount {
  glAccountId: string;
  code: string;
  name: string;
}

/** Resolution state for one payroll result root account. */
export interface HcmPayrollRootResolutionRootState {
  rootGlAccountId: string;
  rootCode: string | null;
  rootName: string | null;
  /** How many dimensions (CC/Dept) in hcm_payroll_dimension_gl_mapping point at this root. */
  usageCount: number;
  /** Always the 19 natures, in the fixed list order (`HCM_PAYROLL_NATURE_TYPES`). */
  natures: HcmPayrollRootNatureItem[];
  /** Postable accounts under the root that no nature adopted, sorted by code. */
  orphanAccounts: HcmPayrollRootOrphanAccount[];
}

/** Response of `GET /hcm/payroll-runs/gl-root-resolution`. */
export interface HcmPayrollRootResolutionStateResponse {
  /** Active assigned CoA version the resolutions belong to. */
  versionId: string;
  /** All rubric types a root resolves, in the fixed list order (`HCM_PAYROLL_NATURE_TYPES`). */
  rubricTypes: HcmPayrollRubricType[];
  roots: HcmPayrollRootResolutionRootState[];
}

/** Tenant-scoped PJ collaborator mapping for FP&A payroll (ALC-2132). */
export interface HcmCollaboratorResponse {
  collaboratorId: string;
  name: string;
  regime: HcmCollaboratorRegime;
  partyId: string | null;
  vendorId: string | null;
  /** Vendor ids pinned at write/resync time. Live party links do not change this. */
  pinnedVendorIds: string[];
  /** Inclusive first competency month (`YYYY-MM`) this interval classifies as Pessoal. */
  effectiveFromMonth: string;
  /** Inclusive last competency month. Null = the interval is still open. */
  effectiveToMonth: string | null;
  status: RowStatus;
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateHcmCollaboratorRequest {
  name: string;
  regime?: HcmCollaboratorRegime;
  partyId?: string | null;
  vendorId?: string | null;
  /** Required vigência. Months before this stay in residual/template bases. */
  effectiveFromMonth: string;
  /** Required with changeReason when a past month restates frozen DESPESA_PROJETADA snapshots. */
  allowReportedPeriodChange?: boolean;
  changeReason?: string | null;
}

export interface UpdateHcmCollaboratorRequest {
  name?: string;
  regime?: HcmCollaboratorRegime;
  partyId?: string | null;
  vendorId?: string | null;
  /** Opens a new interval from this month (closes the current one the month before). */
  effectiveFromMonth?: string;
  /** Close the current interval on this inclusive month without opening a new one. */
  effectiveToMonth?: string | null;
  /** Required with changeReason when the edit restates frozen DESPESA_PROJETADA snapshots. */
  allowReportedPeriodChange?: boolean;
  changeReason?: string | null;
}

export interface ResyncHcmCollaboratorVendorsRequest {
  /** Required with changeReason when the pin change restates frozen DESPESA_PROJETADA snapshots. */
  allowReportedPeriodChange?: boolean;
  changeReason?: string | null;
}

/** Request body of `PUT /hcm/payroll-runs/gl-root-resolution/:rootGlAccountId`. */
export interface SetHcmPayrollRootResolutionRequest {
  versionId?: string | null;
  entityId?: string | null;
  bookId?: string | null;
  rubricType: HcmPayrollRubricType;
  /** Must be an ACTIVE, postable, expense-family descendant of the root. */
  glAccountId: string;
}

/**
 * Stable error codes for payroll CoA-scope resolution (ALC-1999).
 *
 * Classify on `code`, never by matching message text. A missing mapping for a
 * resolved (entity, CoA version) scope fails closed — it never falls back to
 * another version's mapping or to the legacy tenant-wide resolver.
 *
 * `PAYROLL_MAPPING_ACCOUNT_WRONG_VERSION` means an account id is not a row of
 * the resolved version. `PAYROLL_COA_VERSION_NOT_IN_FORCE` means the requested
 * version is not on the entity's assignment history.
 */
export const PayrollCoaMappingErrorCode = {
  PAYROLL_ACCOUNTING_SCOPE_ENTITY_REQUIRED: 'PAYROLL_ACCOUNTING_SCOPE_ENTITY_REQUIRED',
  PAYROLL_MAPPING_MISSING: 'PAYROLL_MAPPING_MISSING',
  PAYROLL_MAPPING_ACCOUNT_WRONG_VERSION: 'PAYROLL_MAPPING_ACCOUNT_WRONG_VERSION',
  PAYROLL_COA_VERSION_NOT_IN_FORCE: 'PAYROLL_COA_VERSION_NOT_IN_FORCE',
} as const;

export type PayrollCoaMappingErrorCode =
  (typeof PayrollCoaMappingErrorCode)[keyof typeof PayrollCoaMappingErrorCode];

/** Operational PJ registration; FP&A positions are planned and linked separately. */
export interface HcmCreatePjCollaboratorRequest {
  entityId: string;
  vendor:
    | { vendorId: string }
    | Pick<CreateVendorRequest, 'vendorCode' | 'legalName' | 'cnpj' | 'taxRegime'>;
  contract: {
    name: string;
    monthlyAmount: string;
    startMonth: string;
    endMonth?: string | null;
    departmentId?: string | null;
    costCenterId?: string | null;
    managerialAccountId?: string | null;
  };
  glAccountId: string;
  paymentTermDays?: number | null;
  expectedDueDayOfMonth?: number;
}

/**
 * Destination a non-tax payroll deduction resolves to by its class when it has
 * no explicit mapping (ADR 0020).
 */
export type PayrollDeductionAutomaticDestination =
  'ASSET_SETTLEMENT' | 'EXPENSE_RECOVERY' | 'REPASSE' | 'PROVISION_SETTLEMENT';

/** One row of `GET /hcm/payroll-runs/deduction-rubrics`. */
export interface PayrollDeductionRubricResponse {
  providerCode: string;
  providerName: string;
  /** Distinct rubric name variants sharing this code (e.g. "eConsignado 2/3/4"). */
  providerNames: string[];
  internalCategory: string | null;
  occurrences: number;
  totalAmount: number;
  /** ISO timestamp of the latest import that carried the rubric. */
  lastSeenAt: string | null;
  /**
   * Destination the deduction class resolves to (ADR 0025: every run type). Null when the class
   * is unknown or when name variants sharing the code disagree (legacy Tako `OTHER` rows).
   */
  automaticDestination: PayrollDeductionAutomaticDestination | null;
}
